<script setup>
// Pole ilości produktu: gramy albo porcje („2 × sztuka"). Na zewnątrz zawsze
// wychodzą gramy — API składników i zestawów zna tylko `weight`. Przełącznik
// pojawia się, gdy produkt ma wagę porcji; bez niej pole jest zwykłymi gramami.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
    hasPortion, portionName, defaultMode, parseAmount, toGrams, nicePortions, formatNumber,
} from '@/composables/usePortions.js';

const { t } = useI18n();

const props = defineProps({
    // Waga w gramach.
    modelValue: { type: [Number, String], default: '' },
    product: { type: Object, default: null },
    id: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    // Pole nowej pozycji: po wyborze innego produktu ilość wraca do domyślnej
    // (1 porcja albo `fallback` gramów). W wierszu istniejącego składnika — nie.
    resetOnProduct: { type: Boolean, default: false },
    fallback: { type: Number, default: 100 },
    // Mniejszy wariant do wierszy tabeli składu.
    compact: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: false },
});
// `commit` — gotowa wartość (Enter albo wyjście z pola), dla wierszy zapisujących od razu.
const emit = defineEmits(['update:modelValue', 'commit', 'enter']);

const root = ref(null);
const input = ref(null);
const mode = ref('g');
const text = ref('');
let lastEmitted = null;

const portionable = computed(() => hasPortion(props.product));
const grams = computed(() => {
    const n = parseAmount(text.value);
    return n > 0 ? toGrams(n, mode.value, props.product) : null;
});

const push = () => {
    lastEmitted = grams.value;
    emit('update:modelValue', grams.value ?? '');
};

// Ustawia pole z gramów: porcje tylko wtedy, gdy wychodzi ładna liczba.
const fromGrams = (weight, preferPortion) => {
    const portions = preferPortion ? nicePortions(weight, props.product) : null;
    if (portions != null) {
        mode.value = 'portion';
        text.value = formatNumber(portions);
    } else {
        mode.value = 'g';
        text.value = weight === '' || weight == null ? '' : String(Math.round(Number(weight)));
    }
    lastEmitted = weight === '' || weight == null ? null : Math.round(Number(weight));
};

fromGrams(props.modelValue, defaultMode(props.product) === 'portion');

watch(() => props.modelValue, (w) => {
    const n = w === '' || w == null ? null : Math.round(Number(w));
    if (n === lastEmitted) return;
    fromGrams(w, mode.value === 'portion' || defaultMode(props.product) === 'portion');
});

watch(() => props.product?.uuid, (now, before) => {
    if (now === before) return;
    if (props.resetOnProduct) {
        if (defaultMode(props.product) === 'portion') {
            mode.value = 'portion';
            text.value = '1';
        } else if (mode.value === 'portion' || !text.value) {
            mode.value = 'g';
            text.value = String(props.fallback);
        }
        push();
    } else if (!portionable.value && mode.value === 'portion') {
        mode.value = 'g';
        text.value = grams.value == null ? '' : String(grams.value);
    }
});

// Przełączenie trybu przelicza wpisaną ilość, żeby waga się nie zmieniła.
const toggle = () => {
    if (!portionable.value || props.disabled) return;
    const w = grams.value;
    if (mode.value === 'g') {
        mode.value = 'portion';
        text.value = w ? formatNumber(Math.round((w / Number(props.product.portion_weight)) * 100) / 100) : '1';
    } else {
        mode.value = 'g';
        text.value = w ? String(w) : String(props.fallback);
    }
    push();
    input.value?.focus();
};

const onInput = (event) => {
    text.value = event.target.value;
    push();
};

const commit = () => emit('commit', grams.value);

// Wyjście z całego pola (nie przejście na przełącznik) zatwierdza wartość.
const onFocusOut = (event) => {
    if (root.value?.contains(event.relatedTarget)) return;
    commit();
};

const onEnter = () => {
    commit();
    emit('enter', grams.value);
};

const unitLabel = computed(() => (mode.value === 'portion' ? portionName(props.product) : 'g'));
const hint = computed(() => (mode.value === 'portion' && grams.value ? `${grams.value} g` : null));

// Pole otwierane kliknięciem (edycja wagi w wierszu) od razu przyjmuje fokus.
onMounted(() => { if (props.autofocus) input.value?.focus(); });

defineExpose({ focus: () => input.value?.focus() });
</script>

<template>
    <span ref="root" class="flex items-center gap-1 min-w-0" @focusout="onFocusOut">
        <input
            :id="id"
            ref="input"
            :value="text"
            type="text"
            :inputmode="mode === 'portion' ? 'decimal' : 'numeric'"
            class="v2-input v2-input--num text-right min-w-0"
            :class="compact ? '!w-[64px] !py-1 !px-2' : 'flex-1'"
            :disabled="disabled"
            :title="hint ?? undefined"
            @input="onInput"
            @keydown.enter.prevent="onEnter"
        />
        <button
            v-if="portionable"
            type="button"
            class="shrink-0 max-w-[76px] truncate rounded-full border text-[11px] font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#C0764B]"
            :class="[
                compact ? 'h-6 px-1.5' : 'h-[26px] px-2',
                mode === 'portion'
                    ? 'border-[#211E1A] bg-[#211E1A] text-[#FBF9F4]'
                    : 'border-[#E4DDD1] text-[#6B655C] hover:border-[#211E1A] hover:text-[#211E1A]',
            ]"
            :title="mode === 'portion'
                ? t('common.amount.portionMode', { portion: portionName(product), weight: product.portion_weight })
                : t('common.amount.gramMode', { portion: portionName(product), weight: product.portion_weight })"
            :aria-label="t('common.amount.unitToggle', { unit: unitLabel })"
            :disabled="disabled"
            @click="toggle"
        >{{ unitLabel }}</button>
        <span v-else class="shrink-0 text-[11.5px] text-[#B8B0A3] w-3">g</span>
    </span>
</template>
